import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { requestJson } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";

export type RiderOrderItem = {
  id: string;
  name: string;
  quantity: number;
};

export type RiderDeliveryOrder = {
  id: string;
  dailyNumber: number;
  status: string;
  total: number;
  currency: string;
  paymentMode: string;
  paymentStatus: string;
  contactName: string | null;
  contactPhone: string | null;
  items: RiderOrderItem[];
};

export type RiderDelivery = {
  delivery: {
    id: string;
    orderId: string;
    state: string;
    // Delivery address snapshot: canonical address copy on the Delivery row.
    addressSnapshot: {
      label?: string | null;
      line1?: string;
      line2?: string | null;
      city?: string | null;
      country?: string;
      lat?: string | number | null;
      lng?: string | number | null;
      notes?: string | null;
    } | null;
    lastLat: string | null;
    lastLng: string | null;
    assignedAt: string | null;
    acceptedAt: string | null;
    enRouteAt: string | null;
    deliveredAt: string | null;
    failedAt: string | null;
    // What the Rider reported about the cash on completing a COD Delivery.
    cashCollection: "collected" | "not_collected" | null;
    cashCollectionAt: string | null;
  };
  order: RiderDeliveryOrder;
};

/**
 * Whether completing this Delivery means collecting cash from the customer.
 * `pay_later` is cash on delivery; a `pay_now` Order was already paid online.
 */
export function isCashOnDelivery(delivery: RiderDelivery): boolean {
  return delivery.order.paymentMode === "pay_later";
}

export function useMyRiderDeliveries() {
  return useQuery({
    queryKey: queryKeys.riderDeliveries.all(),
    queryFn: () => requestJson<{ deliveries: RiderDelivery[] }>("/api/deliveries/mine"),
    select: (data) => data.deliveries,
    refetchInterval: 30_000,
  });
}

export function useRiderDeliveryHistory() {
  return useQuery({
    queryKey: queryKeys.riderDeliveries.history(),
    queryFn: () => requestJson<{ deliveries: RiderDelivery[] }>("/api/deliveries/history"),
    select: (data) => data.deliveries,
  });
}

function transitionMutation(action: string) {
  return (deliveryId: string) =>
    requestJson<{ delivery: RiderDelivery["delivery"] }>(
      `/api/deliveries/${deliveryId}/${action}`,
      { method: "POST" },
    );
}

export function useDeliveryAccept() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: transitionMutation("accept"),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.riderDeliveries.all() }),
  });
}

export function useDeliveryDecline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: transitionMutation("decline"),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.riderDeliveries.all() }),
  });
}

export function useDeliveryStart() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: transitionMutation("start"),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.riderDeliveries.all() }),
  });
}

export function useDeliveryArrive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: transitionMutation("arrive"),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.riderDeliveries.all() }),
  });
}

export function useDeliveryComplete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ deliveryId, cashCollected }: { cashCollected?: boolean; deliveryId: string }) =>
      requestJson<{ delivery: RiderDelivery["delivery"] }>(
        `/api/deliveries/${deliveryId}/complete`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ cashCollected }),
        },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.riderDeliveries.all() }),
  });
}

export function useDeliveryFail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ deliveryId, reason }: { deliveryId: string; reason?: string }) =>
      requestJson<{ delivery: RiderDelivery["delivery"] }>(`/api/deliveries/${deliveryId}/fail`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.riderDeliveries.all() }),
  });
}

export const PRE_READY_ORDER_STATES = new Set(["pending", "accepted", "in_progress"]);

export function isOrderReadyForPickup(delivery: RiderDelivery): boolean {
  return delivery.order.status === "ready";
}

export function isDeliveryEnRoute(delivery: RiderDelivery): boolean {
  return delivery.delivery.state === "en_route" || delivery.delivery.state === "arrived";
}

export function groupRiderDeliveries(deliveries: RiderDelivery[]): {
  waiting: RiderDelivery[];
  ready: RiderDelivery[];
  enRoute: RiderDelivery[];
} {
  const waiting: RiderDelivery[] = [];
  const ready: RiderDelivery[] = [];
  const enRoute: RiderDelivery[] = [];

  for (const entry of deliveries) {
    if (isDeliveryEnRoute(entry)) {
      enRoute.push(entry);
    } else if (isOrderReadyForPickup(entry)) {
      ready.push(entry);
    } else {
      waiting.push(entry);
    }
  }

  return { waiting, ready, enRoute };
}
